import { LegacyPortalSpwnEcService } from "@spwn-portal/grpc";
import { createConnectClient } from "utility/connectWeb";

import { loadCustomerAccessToken } from "./customerAccountAuthorize";

import type { LegacyPortalLiveCommerceCart } from "@spwn-portal/grpc";

/** SPWN バックエンドが返す Shopify カート */
export type ShopifyLiveCommerceCart = LegacyPortalLiveCommerceCart;

export type ShopifyCartLineInput = {
  /** Shopify の ProductVariant GID。ProductData の _id と同じもの */
  merchandiseId: string;
  quantity: number;
};

/** 配信画面のカートから、同期 API に渡す lines を作る */
export const toShopifyCartLineInputs = (
  items: { _id?: string; count: number }[]
): ShopifyCartLineInput[] =>
  items
    .filter(
      (item): item is { _id: string; count: number } =>
        item._id !== undefined && item.count > 0
    )
    .map((item) => ({
      merchandiseId: item._id,
      quantity: item.count,
    }));

const cartLinesKey = (lines: ShopifyCartLineInput[]): string =>
  JSON.stringify(
    [...lines].sort((a, b) => a.merchandiseId.localeCompare(b.merchandiseId))
  );

/** Shopify 側のカートが、渡した lines と同じ内容か */
export const matchesShopifyCartLines = (
  cart: ShopifyLiveCommerceCart,
  lines: ShopifyCartLineInput[]
): boolean => {
  const current: ShopifyCartLineInput[] = cart.lines.map((line) => ({
    merchandiseId: line.merchandiseId,
    quantity: line.quantity,
  }));
  return cartLinesKey(current) === cartLinesKey(lines);
};

/**
 * cartId はイベント単位で保持する。
 * Shopify のカートはチャネルでもイベントでも分離されないため、
 * 別イベントの商品が同じカートに混ざらないよう SPWN 側で分けている。
 */
const cartIdStorageKey = (eventId: string) =>
  `spwn.liveCommerce.shopifyCartId.${eventId}`;

/** localStorage はプライベートブラウジング等で例外を投げるため必ず握る */
const loadShopifyCartId = (eventId: string): string | undefined => {
  try {
    return localStorage.getItem(cartIdStorageKey(eventId)) ?? undefined;
  } catch {
    return undefined;
  }
};

const removeShopifyCartId = (eventId: string): void => {
  try {
    localStorage.removeItem(cartIdStorageKey(eventId));
  } catch {
    // 消せなくても、次の同期でバックエンドが新しいカートに差し替える
  }
};

const saveShopifyCartId = (eventId: string, cartId: string): void => {
  try {
    localStorage.setItem(cartIdStorageKey(eventId), cartId);
  } catch {
    // 保存できなくても毎回カートを作り直すだけで、購入導線は止まらない
  }
};

/** 数量の +/- を連打されたときにリクエストが飛び続けないよう間引く */
const SYNC_DEBOUNCE_MS = 500;

type EventSyncState = {
  timer: ReturnType<typeof setTimeout> | undefined;
  /** 直近で同期に成功した内容。同じ内容なら送らない */
  lastSyncedKey: string | undefined;
  /** 送信待ちの内容。連打された場合は最後のものだけを送る */
  pending: { key: string; lines: ShopifyCartLineInput[] } | undefined;
  /** 直近で成功したカート。checkoutUrl は同期完了後にだけ使う */
  lastCart: ShopifyLiveCommerceCart | undefined;
  /** 同時に cartCreate が走らないよう直列化するためのキュー */
  queue: Promise<unknown>;
  listeners: Set<(cart: ShopifyLiveCommerceCart | undefined) => void>;
};

/**
 * 同期の状態はモジュールスコープで持つ。
 *
 * useLiveCommerce は複数のコンポーネントから呼ばれるため、フックのインスタンス
 * ごとに状態を持つと、それぞれが独立に cartCreate を投げてカートが複数できる。
 */
const syncStates = new Map<string, EventSyncState>();

const getSyncState = (eventId: string): EventSyncState => {
  const existing = syncStates.get(eventId);
  if (existing !== undefined) return existing;
  const created: EventSyncState = {
    timer: undefined,
    lastSyncedKey: undefined,
    pending: undefined,
    lastCart: undefined,
    queue: Promise.resolve(),
    listeners: new Set(),
  };
  syncStates.set(eventId, created);
  return created;
};

/**
 * カートの同期を予約する。呼び出し元が複数あっても、イベントごとに
 * 1本のリクエストへまとめられる。
 *
 * 同期は「あるべき状態」を送る宣言的な API なので、途中で失敗しても
 * 次の操作で自動的に整合する。購入導線を止めないためエラーは握り潰す。
 */
export const scheduleShopifyLiveCommerceCartSync = (input: {
  eventId: string;
  lines: ShopifyCartLineInput[];
}): void => {
  const state = getSyncState(input.eventId);
  const linesKey = JSON.stringify(input.lines);

  // 送信待ちを最新の内容で置き換える。連打の途中経過は送らない
  if (state.timer !== undefined) clearTimeout(state.timer);
  state.timer = undefined;

  // 同期済みの内容に戻ってきたら、送信待ちごと取り消す。
  // ここで単に return すると、1つ前の内容を送る timer が残って Shopify とずれる
  if (state.lastSyncedKey === linesKey) {
    state.pending = undefined;
    return;
  }

  // カートが空で、まだ Shopify 側にカートを作っていないなら何もしない。
  // ページを開いただけで空カートを作るのを避ける
  if (
    input.lines.length === 0 &&
    loadShopifyCartId(input.eventId) === undefined
  ) {
    state.pending = undefined;
    return;
  }

  state.pending = { key: linesKey, lines: input.lines };
  state.timer = setTimeout(() => {
    state.timer = undefined;
    const { pending } = state;
    if (pending === undefined) return;
    state.pending = undefined;

    state.queue = state.queue
      .then(async () => {
        await syncShopifyLiveCommerceCartNow({
          eventId: input.eventId,
          lines: pending.lines,
        });
        // 成功したものだけを「送信済み」として覚える
        state.lastSyncedKey = pending.key;
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  }, SYNC_DEBOUNCE_MS);
};

/**
 * Shopify のカートを lines の内容に一致させる。
 *
 * 差分ではなく「あるべき状態」を送る。lines に含めなかった商品はカートから
 * 取り除かれるので、配信画面のカート (pseudoCart) をそのまま渡せばよい。
 *
 * cartId が期限切れの場合はバックエンドが新しいカートを作って返すため、
 * 返ってきた cartId で必ず保存を上書きする。
 * ログイン必須で、ID トークンは connect クライアントの interceptor が付ける。
 */
export const syncShopifyLiveCommerceCartNow = async (input: {
  eventId: string;
  lines: ShopifyCartLineInput[];
  /**
   * 購入直前の同期で true にする。視聴中のイベントが明細行のプロパティとして注文に残り、
   * 購入コメントの投稿先になる。デバウンスされる通常の同期では不要。
   */
  attachEvent?: boolean;
}): Promise<ShopifyLiveCommerceCart> => {
  const cartId = loadShopifyCartId(input.eventId);
  const client = createConnectClient(LegacyPortalSpwnEcService);
  const customerAccessToken = await loadCustomerAccessToken(sessionStorage);

  const { cart } = await client.syncLiveCommerceCart({
    ...(cartId === undefined ? {} : { cartId }),
    lines: input.lines,
    ...(customerAccessToken === undefined ? {} : { customerAccessToken }),
    ...(input.attachEvent === true ? { eventId: input.eventId } : {}),
  });
  if (cart === undefined) {
    throw new Error("failed to sync shopify cart");
  }

  saveShopifyCartId(input.eventId, cart.id);
  const state = getSyncState(input.eventId);
  state.lastCart = cart;
  state.listeners.forEach((listener) => listener(cart));
  return cart;
};

/**
 * 同期完了を購読する。popup ブロックを避けるため、購入クリック時点で
 * checkoutUrl が既に取れている必要がある。
 */
export const subscribeShopifyLiveCommerceCart = (
  eventId: string,
  listener: (cart: ShopifyLiveCommerceCart | undefined) => void
): (() => void) => {
  const state = getSyncState(eventId);
  state.listeners.add(listener);
  if (state.lastCart !== undefined) {
    listener(state.lastCart);
  }
  return () => {
    state.listeners.delete(listener);
  };
};

/**
 * 購入完了後に、注文済みのカートを忘れる。
 *
 * 注文済みのカート ID へ空の内容を送ると、Shopify の挙動によっては
 * そのイベントで二度と同期できなくなるため、ID と同期状態を破棄する。
 * 送信待ちも取り消すので、カートを空にする前に呼ぶこと。
 */
export const clearShopifyLiveCommerceCart = (eventId: string): void => {
  const state = getSyncState(eventId);
  if (state.timer !== undefined) clearTimeout(state.timer);
  state.timer = undefined;
  state.pending = undefined;
  state.lastSyncedKey = undefined;
  state.lastCart = undefined;
  removeShopifyCartId(eventId);
  state.listeners.forEach((listener) => listener(undefined));
};
